import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved,
  faGem,
  faCertificate,
  faArrowTrendUp,
  faChevronLeft,
  faChevronRight,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import Button from '../common/Button';
import { transitions } from '../../utils/motion';

export const CURATED_ARTWORKS = [
  {
    id: 'tittat-01',
    tokenTag: 'TitTat #01',
    title: 'The Sovereign Heritage',
    artist: 'Mahendra Raj Shrestha',
    medium: 'Master Oil & Gold Leaf on Linen',
    dimensions: '120 × 160 cm (2024)',
    image: '/image_1.png',
    valuation: 'NPR 3,850,000',
    totalTokens: 100,
    pricePerToken: 'NPR 38,500',
    tokensAvailable: 14,
    cagr: '+28.6% Annualized',
    vaultId: 'VAULT-KTM-001',
    provenance: 'Acquired from Kathmandu National Gallery Retrospective; secured in climate-controlled biometric vault.',
    category: 'Classical Figuration',
    badge: 'FLAGSHIP ASSET',
    accentColor: '#ded9d9ff',
  },
  {
    id: 'tittat-02',
    tokenTag: 'TitTat #02',
    title: 'Silent Resonance',
    artist: 'Aayush K. Sharma',
    medium: 'Monochrome Tempera & Silver Leaf',
    dimensions: '100 × 140 cm (2025)',
    image: '/image_2.png',
    valuation: 'NPR 2,900,000',
    totalTokens: 100,
    pricePerToken: 'NPR 29,000',
    tokensAvailable: 28,
    cagr: '+34.2% Annualized',
    vaultId: 'VAULT-KTM-002',
    provenance: 'Featured at Triennale International; underwritten by Lloyd’s of London syndicate custody.',
    category: 'Neo-Constructivism',
    badge: 'HIGH LIQUIDITY',
    accentColor: '#38bdf8',
  },
  {
    id: 'tittat-03',
    tokenTag: 'TitTat #03',
    title: 'Terracotta Genesis',
    artist: 'Bipana Shakya',
    medium: 'Organic Earth Pigments & Resin',
    dimensions: '110 × 150 cm (2025)',
    image: '/image_3.png',
    valuation: 'NPR 4,600,000',
    totalTokens: 200,
    pricePerToken: 'NPR 23,000',
    tokensAvailable: 42,
    cagr: '+22.8% Annualized',
    vaultId: 'VAULT-KTM-003',
    provenance: 'Registered on SHA-256 smart-contract registry; authenticated by Nepal Academy of Fine Arts.',
    category: 'Himalayan Expressionism',
    badge: 'DIVIDEND YIELD',
    accentColor: '#10b981',
  },
  {
    id: 'tittat-04',
    tokenTag: 'TitTat #04',
    title: 'The Great Horizon',
    artist: 'Laxman Kumar Pradhan',
    medium: 'Monumental Panoramic Acrylic on Silk',
    dimensions: '200 × 320 cm (2026)',
    image: '/image_4.png',
    valuation: 'NPR 9,250,000',
    totalTokens: 250,
    pricePerToken: 'NPR 37,000',
    tokensAvailable: 65,
    cagr: '+41.5% Annualized',
    vaultId: 'VAULT-KTM-004',
    provenance: 'Premier 2026 flagship acquisition; institutional reserve asset with quarterly royalty distributions.',
    category: 'Monumental Heritage',
    badge: 'INSTITUTIONAL RESERVE',
    accentColor: '#c084fc',
  },
];

/**
 * CuratedMasterpieces
 * Natural-flow Interactive 3D Masterpiece Exhibition Stage.
 * Hardened against scroll-jacking traps, navbar collisions, and blank screen voids.
 */
export default function CuratedMasterpieces() {
  const shouldReduceMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const activeArtwork = CURATED_ARTWORKS[activeIndex] || CURATED_ARTWORKS[0];

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % CURATED_ARTWORKS.length);
  }, []);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + CURATED_ARTWORKS.length) % CURATED_ARTWORKS.length);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev]);

  // Gentle 5s auto-cycle with pause on hover
  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      handleNext();
    }, 5500);
    return () => clearInterval(timer);
  }, [isAutoPlaying, handleNext]);

  return (
    <section
      className="lp-curated-section"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Ambient Radial Backdrop Glow */}
      <div
        className="lp-curated-ambient-glow"
        style={{
          background: `radial-gradient(circle at 75% 50%, ${activeArtwork.accentColor}18 0%, transparent 65%)`,
        }}
        aria-hidden="true"
      />

      <div className="lp-container lp-curated-container">
        {/* Section Header with Proper Navbar Clearance */}
        <div className="lp-curated-header">
          <div className="lp-curated-header-left">
            <span className="lp-section-badge">
              <FontAwesomeIcon icon={faGem} style={{ marginRight: '6px' }} />
              CURATED MASTERPIECE EXHIBITION
            </span>
            <h2 className="lp-curated-title">
              The <span className="lp-gradient-text">Sovereign Collection</span>
            </h2>
          </div>

          {/* Scrubber Tabs & Navigation Chevrons */}
          <div className="lp-curated-controls-bar">
            <div className="lp-curated-scrubber" role="tablist" aria-label="Curated Artworks">
              {CURATED_ARTWORKS.map((art, idx) => (
                <button
                  key={art.id}
                  role="tab"
                  aria-selected={activeIndex === idx}
                  className={`lp-scrubber-pill ${activeIndex === idx ? 'active' : ''}`}
                  onClick={() => setActiveIndex(idx)}
                >
                  <img src={art.image} alt={art.title} className="lp-scrubber-thumb" />
                  <span className="lp-scrubber-label font-mono">0{idx + 1}</span>
                  <span className="lp-scrubber-name">{art.tokenTag}</span>
                </button>
              ))}
            </div>

            <div className="lp-curated-arrows">
              <button
                type="button"
                className="lp-carousel-arrow-btn"
                onClick={handlePrev}
                aria-label="Previous Artwork"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                type="button"
                className="lp-carousel-arrow-btn"
                onClick={handleNext}
                aria-label="Next Artwork"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>

        {/* 2-Column Responsive Stage */}
        <div className="lp-curated-stage">
          {/* Left Column: Financial Telemetry & Editorial Narrative */}
          <div className="lp-curated-narrative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeArtwork.id}
                initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: shouldReduceMotion ? 0 : -12 }}
                transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
                className="lp-curated-card-info"
              >
                <div className="lp-curated-tags-row">
                  <span
                    className="lp-curated-badge-pill font-mono"
                    style={{
                      borderColor: `${activeArtwork.accentColor}55`,
                      color: activeArtwork.accentColor,
                    }}
                  >
                    {activeArtwork.badge}
                  </span>
                  <span className="lp-curated-category font-mono">
                    {activeArtwork.category}
                  </span>
                </div>

                <h3 className="lp-curated-art-title">{activeArtwork.title}</h3>
                <p className="lp-curated-art-artist">
                  By <strong>{activeArtwork.artist}</strong> • {activeArtwork.medium}
                </p>
                <p className="lp-curated-dimensions font-mono">{activeArtwork.dimensions}</p>

                <p className="lp-curated-provenance-text">{activeArtwork.provenance}</p>

                {/* Financial Metrics Matrix */}
                <div className="lp-curated-metrics-grid">
                  <div className="lp-curated-metric-card">
                    <span className="lp-metric-label">Asset Valuation</span>
                    <span className="lp-metric-value font-mono">
                      {activeArtwork.valuation}
                    </span>
                  </div>

                  <div className="lp-curated-metric-card">
                    <span className="lp-metric-label">TitTat Share Price</span>
                    <span
                      className="lp-metric-value font-mono"
                      style={{ color: activeArtwork.accentColor }}
                    >
                      {activeArtwork.pricePerToken}
                    </span>
                  </div>

                  <div className="lp-curated-metric-card">
                    <span className="lp-metric-label">Annual Appreciation</span>
                    <span className="lp-metric-value font-mono" style={{ color: 'var(--success, #10b981)' }}>
                      <FontAwesomeIcon icon={faArrowTrendUp} style={{ marginRight: '4px' }} />
                      {activeArtwork.cagr}
                    </span>
                  </div>

                  <div className="lp-curated-metric-card">
                    <span className="lp-metric-label">Custody Vault ID</span>
                    <span className="lp-metric-value font-mono">
                      <FontAwesomeIcon icon={faShieldHalved} style={{ marginRight: '6px', color: '#06b6d4' }} />
                      {activeArtwork.vaultId}
                    </span>
                  </div>
                </div>

                {/* Fractional Liquidity Bar */}
                <div className="lp-curated-token-bar-wrap">
                  <div className="lp-token-bar-header font-mono">
                    <span>Available Liquidity</span>
                    <span>
                      {activeArtwork.tokensAvailable} / {activeArtwork.totalTokens} Tokens
                    </span>
                  </div>
                  <div className="lp-token-progress-track">
                    <motion.div
                      className="lp-token-progress-fill"
                      initial={{ width: 0 }}
                      animate={{
                        width: `${((activeArtwork.totalTokens - activeArtwork.tokensAvailable) / activeArtwork.totalTokens) * 100}%`,
                      }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                      style={{ backgroundColor: activeArtwork.accentColor }}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="lp-curated-actions">
                  <Button
                    to="/login"
                    variant="primary"
                    size="md"
                    trailingIcon="→"
                  >
                    Invest in Fractions
                  </Button>
                  <Button
                    to="/about"
                    variant="secondary"
                    size="md"
                  >
                    <FontAwesomeIcon icon={faCertificate} style={{ marginRight: '6px' }} />
                    View Certificate
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right Column: 3D Spatial Masterpiece Stage */}
          <div className="lp-curated-deck-viewport">
            <div className="lp-curated-3d-stage">
              {CURATED_ARTWORKS.map((artwork, idx) => {
                const diff = (idx - activeIndex + CURATED_ARTWORKS.length) % CURATED_ARTWORKS.length;
                // Position logic: 0 = active, 1 = right card, 3 (or -1) = left card, 2 = hidden/back
                let xOffset = 0;
                let zOffset = 0;
                let rotateY = 0;
                let scale = 1;
                let opacity = 1;
                let zIndex = 10;

                const sideOffset = isMobile ? 18 : 110;
                const sideRotate = shouldReduceMotion || isMobile ? 0 : 16;
                const sideScale = isMobile ? 0.94 : 0.88;

                if (diff === 0) {
                  // Active Center Card
                  xOffset = 0;
                  zOffset = 40;
                  rotateY = 0;
                  scale = 1;
                  opacity = 1;
                  zIndex = 10;
                } else if (diff === 1) {
                  // Right Perspective Card
                  xOffset = sideOffset;
                  zOffset = -50;
                  rotateY = sideRotate;
                  scale = sideScale;
                  opacity = isMobile ? 0.35 : 0.65;
                  zIndex = 5;
                } else if (diff === CURATED_ARTWORKS.length - 1) {
                  // Left Perspective Card
                  xOffset = -sideOffset;
                  zOffset = -50;
                  rotateY = -sideRotate;
                  scale = sideScale;
                  opacity = isMobile ? 0.35 : 0.65;
                  zIndex = 5;
                } else {
                  // Hidden Background
                  xOffset = 0;
                  zOffset = -120;
                  rotateY = 0;
                  scale = 0.75;
                  opacity = 0;
                  zIndex = 1;
                }

                return (
                  <motion.div
                    key={artwork.id}
                    className={`lp-3d-card-wrapper ${diff === 0 ? 'is-active' : ''}`}
                    animate={{
                      x: xOffset,
                      z: zOffset,
                      rotateY,
                      scale,
                      opacity,
                    }}
                    transition={transitions.smooth}
                    style={{ zIndex }}
                    onClick={() => setActiveIndex(idx)}
                  >
                    <div className="lp-3d-art-frame">
                      {/* Holographic Sheen */}
                      <div className="lp-art-sheen" aria-hidden="true" />

                      {/* Header Badge */}
                      <div className="lp-art-header-pill">
                        <span className="lp-art-token-badge font-mono">{artwork.tokenTag}</span>
                        <span className="lp-art-vault-badge font-mono">
                          <FontAwesomeIcon icon={faLock} style={{ fontSize: '10px' }} />
                          SECURED
                        </span>
                      </div>

                      {/* Masterpiece Canvas */}
                      <div className="lp-art-image-container">
                        <img
                          src={artwork.image}
                          alt={artwork.title}
                          className="lp-art-canvas-img"
                        />
                      </div>

                      {/* Bottom Plaque */}
                      <div className="lp-art-plaque">
                        <div className="lp-plaque-details">
                          <h4 className="lp-plaque-title">{artwork.title}</h4>
                          <span className="lp-plaque-artist">{artwork.artist}</span>
                        </div>
                        <div className="lp-plaque-val font-mono">{artwork.valuation}</div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Status Pips */}
        <div className="lp-curated-bottom-status">
          <span className="lp-status-step font-mono">
            COLLECTION 0{activeIndex + 1} / 0{CURATED_ARTWORKS.length}
          </span>
          <div className="lp-curated-pips">
            {CURATED_ARTWORKS.map((_, i) => (
              <button
                key={i}
                type="button"
                className={`lp-pip-dot ${activeIndex === i ? 'active' : ''}`}
                onClick={() => setActiveIndex(i)}
                aria-label={`View Artwork ${i + 1}`}
              />
            ))}
          </div>
          <span className="lp-status-scroll-hint font-mono">
            AUTONOMOUS PROVENANCE VERIFIED
          </span>
        </div>
      </div>
    </section>
  );
}
